import {ethers} from "ethers";
import contractData from "./contract-builds/StakingContract";
import tokenData from "./contract-builds/IERC20";

const NETWORK_ROPSTEN = "ropsten";
const NETWORK_DEVELOPMENT = "development";
const NETWORK_LIVE = "live";

export default class StakingContract {
  provider;
  stakingContract;
  tokenContract;

  constructor() {
    let canInitialize = false;
    switch (StakingContract.getProvider()) {
      case NETWORK_ROPSTEN:
      case NETWORK_LIVE:
      default: canInitialize = this.setLocalProvider();
    }

    if ( !canInitialize ) {
      this.setDummyProvider();
    }

    try {
      this.stakingContract = new ethers.Contract(StakingContract.getEnv("REACT_APP_ETH_DEPLOYED_CONTRACT"), contractData.abi, this.provider);
    } catch (e) {
      console.warn("could not initialize stakingContract: ", StakingContract.getEnv("REACT_APP_ETH_DEPLOYED_CONTRACT"));
      throw e;
    }

    try {
      this.tokenContract = new ethers.Contract(StakingContract.getEnv("REACT_APP_ETH_DEPLOYED_TOKEN"), tokenData.abi, this.provider);
    } catch (e) {
      console.warn("could not initialize stakingContract: ", StakingContract.getEnv("REACT_APP_ETH_DEPLOYED_TOKEN"));
      throw e;
    }
  }

  static hasMetamask() {
    return typeof window.web3 !== 'undefined';
  }

   async metamaskEnabled() {
    try {
      await this.provider.getSigner().getAddress();
      return true;
    } catch (e) {
      return false;
    }
  }

  static enableMetamask() {
    return window.ethereum.enable();
  }

  ethBalance() {
    this.setDefaultSigners();
    return this.provider.getSigner().getBalance();
  }

  erdBalance() {
    this.setDefaultSigners();
    return this.provider.getSigner().getAddress()
      .then(address => {
        return this.tokenContract.balanceOf(address)
          .then(bigNumVal => {
            return parseInt(ethers.utils.formatEther(bigNumVal), 10);
          });
      });
  }

  getAllowance() {
    this.setDefaultSigners();
    return this.provider.getSigner().getAddress()
      .then(address => {
        return this.tokenContract.allowance(address, StakingContract.getEnv("REACT_APP_ETH_DEPLOYED_CONTRACT"))
          .then(bigNumVal => {
            return parseInt(ethers.utils.formatEther(bigNumVal), 10);
          });
      });
  }

  getStakeDeposit() {
    this.setDefaultSigners();
    return this.stakingContract.getStakeDeposit()
      .then(res => res)
      .catch(e => {
        console.log("err: ", e);
        return false;
      });
  }

  alreadyStaked() {
    this.setDefaultSigners();
    return this.stakingContract.getStakeDeposit()
      .then(res => {
        console.log("success: ", res);
        return true;
      })
      .catch(e => {
        console.log("err: ", e);
        return false;
      });
  }

  contractTotalStakeLimit() {
    return this.stakingContract.currentStakingLimit()
      .then(bigNumVal => {
        return parseInt(ethers.utils.formatEther(bigNumVal), 10);
      });
  }

  currentTotalStake() {
    return this.stakingContract.currentTotalStake()
      .then(bigNumVal => {
        console.log("total", ethers.utils.formatEther(bigNumVal));
        return parseInt(ethers.utils.formatEther(bigNumVal), 10);
      });
  }

  approveContract(value) {
    this.setDefaultSigners();
    return this.tokenContract.approve(StakingContract.getEnv("REACT_APP_ETH_DEPLOYED_CONTRACT"), ethers.utils.parseEther(value));
  }

  stakeERD(value) {
    this.setDefaultSigners();
    return this.stakingContract.deposit(ethers.utils.parseEther(value))
      .catch(e => console.error("stake err", e));
  }

  setLocalProvider() {
    if ( !window.web3 ) {
      console.warn("metamask not installed");
      return false;
    }
    this.provider = new ethers.providers.Web3Provider(window.web3.currentProvider);
    this.provider.ready.catch(e => console.error("Could not create Web3Provider: ", e));
    return true;
  }

  setDummyProvider() {
    switch (StakingContract.getProvider()) {
      case NETWORK_ROPSTEN:
        this.provider = new ethers.providers.InfuraProvider("ropsten");
        this.provider.ready.catch(e => console.error("Could not create read-only InfuraProvider: ", e));
        break;
      case NETWORK_LIVE:
        this.provider = new ethers.providers.InfuraProvider("homestead");
        this.provider.ready.catch(e => console.error("Could not create read-only InfuraProvider: ", e));
        break;
      default:
        this.provider  = new ethers.providers.JsonRpcProvider("http://127.0.0.1:7545");
        this.provider.ready.catch(e => console.error("Could not create read-only JsonRpcProvider for development mode: ", e));
    }

    console.log(this.provider);
  }

  initiateWithdrawal() {
    this.setDefaultSigners();
    return this.stakingContract.initiateWithdrawal();
  }

  executeWithdrawal() {
    this.setDefaultSigners();
    return this.stakingContract.executeWithdrawal();
  }

  // Owner functions
  toggleRewards(enabled) {
    this.setDefaultSigners();
    return this.stakingContract.toggleRewards(enabled)
  }

  togglePaused(enabled) {
    this.setDefaultSigners();
    if ( enabled ) {
      return this.stakingContract.unpause();
    }
    return this.stakingContract.pause();
  }

  setDefaultSigners() {
    this.stakingContract = this.stakingContract.connect(this.provider.getSigner());
    this.tokenContract = this.tokenContract.connect(this.provider.getSigner());
  }

  fromWei(weiValue) {
    return ethers.utils.formatEther(weiValue);
  }

  static getProvider() {
    switch (process.env.REACT_APP_ETH_PROVIDER) {
      case NETWORK_ROPSTEN:
        return NETWORK_ROPSTEN;
      case NETWORK_LIVE:
        return NETWORK_LIVE;
      default:
        return NETWORK_DEVELOPMENT;
    }
  }

  static getEnv(envVar) {
    const provider = StakingContract.getProvider();
    return process.env[envVar + `_${provider.toUpperCase()}`];
  }
}